// Queue agents: pick users from the directory; stores Entra object ids only
// (names are resolved live wherever the list is displayed).

import { useMemo, useState } from 'react';
import { Search, X } from 'lucide-react';
import type { QueueAgent } from '../../api/client';
import { inputCls } from './common';

export interface AgentOption { id: string; displayName: string; email?: string; jobTitle?: string }

export function AgentsPicker({ value, directory, onChange, max = 100 }: {
  value: QueueAgent[];
  directory: AgentOption[];
  onChange: (v: QueueAgent[]) => void;
  max?: number;
}) {
  const [q, setQ] = useState('');
  const byId = useMemo(() => new Map(directory.map(d => [d.id.toLowerCase(), d])), [directory]);
  const matches = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return [];
    const chosen = new Set(value.map(a => a.id.toLowerCase()));
    return directory
      .filter(d => !chosen.has(d.id.toLowerCase()) && (d.displayName.toLowerCase().includes(s) || (d.email || '').toLowerCase().includes(s)))
      .slice(0, 8);
  }, [q, directory, value]);

  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-1.5">
        {value.length === 0 && <span className="text-xs text-gray-400">No individual agents (the queue may use a group instead).</span>}
        {value.map(a => {
          const d = byId.get(a.id.toLowerCase());
          return (
            <span key={a.id} className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full bg-gray-100 text-xs text-gray-800">
              <span className="truncate max-w-[10rem]" title={d?.email || a.id}>{d?.displayName || `${a.id.slice(0, 8)}…`}</span>
              <label className="inline-flex items-center gap-0.5 text-[10px] text-gray-500" title="Opted in to take calls">
                <input type="checkbox" checked={a.optIn !== false} className="accent-purple-600 w-3 h-3"
                  onChange={e => onChange(value.map(x => (x.id === a.id ? { id: x.id, optIn: e.target.checked } : x)))} />in
              </label>
              <button type="button" onClick={() => onChange(value.filter(x => x.id !== a.id))} className="p-0.5 text-gray-400 hover:text-red-600"><X className="w-3 h-3" /></button>
            </span>
          );
        })}
      </div>
      {value.length < max && (
        <div className="relative">
          <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-2.5" />
          <input type="text" value={q} onChange={e => setQ(e.target.value)} placeholder="Add an agent by name or email…" className={`${inputCls} pl-8`} />
          {matches.length > 0 && (
            <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded shadow-lg max-h-48 overflow-y-auto">
              {matches.map(d => (
                <button key={d.id} type="button" onClick={() => { onChange([...value, { id: d.id.toLowerCase(), optIn: true }]); setQ(''); }}
                  className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 flex items-center justify-between gap-2">
                  <span className="truncate">{d.displayName}</span>
                  <span className="text-xs text-gray-400 truncate">{d.email}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      <p className="text-[11px] text-gray-400 mt-1">{value.length} of max {max} agents.</p>
    </div>
  );
}
